import getBroadcastChannelName from 'src/js/modules/read-only-mode/helpers/get-broadcast-channel-name';
import { uuid } from '@yola/ws-sdk';
import constants from '../constants';

const OPTIONS_SYMBOL = Symbol('app-control-driver-event-options');

class AppControlDriver {
  constructor({ getReadOnlyStatus, inactiveTabBeforeRequestAppControl }) {
    if (!getReadOnlyStatus) throw new Error('getReadOnlyStatus is required');

    this.getReadOnlyStatus = getReadOnlyStatus;
    this.inactiveTabBeforeRequestAppControl = inactiveTabBeforeRequestAppControl;
    this.observers = new Map();

    this.init();
  }

  init() {
    if (!window.BroadcastChannel) {
      // eslint-disable-next-line no-console
      console.warn('BroadcastChannel is not supported');
      return;
    }
    this.broadcastChannel = new BroadcastChannel(getBroadcastChannelName());
    this.id = uuid();

    this.addMessageListener();
  }

  addMessageListener() {
    if (!this.broadcastChannel) return;

    this.broadcastChannel.addEventListener('message', (event) => {
      const { data } = event;
      const readOnlyStatus = this.getReadOnlyStatus();

      switch (data.type) {
        case constants.messages.APP_INSTANCE_LOADED: {
          if (!readOnlyStatus) {
            this.postMessage({
              id: data.id,
              type: constants.messages.APP_INSTANCE_LOADED_RESPONSE,
            });
          }
          break;
        }

        case constants.messages.APP_INSTANCE_LOADED_RESPONSE: {
          if (data.id !== this.id) return;
          this.trigger(constants.events.APP_INSTANCE_LOADED_RESPONSE, { id: this.id });
          break;
        }

        case constants.messages.REQUEST_APP_CONTROL: {
          if (readOnlyStatus) return;
          if (this.inactiveTabBeforeRequestAppControl) {
            const preprocessorPromise = new Promise((resolve, reject) => {
              this.postMessage({
                id: data.id,
                type: constants.messages.REQUEST_APP_CONTROL_RESPONSE,
                status: constants.statuses.REQUEST_CONTROL_PENDING,
              });
              this.inactiveTabBeforeRequestAppControl({ data, resolve, reject });
            });

            preprocessorPromise
              .then(() => {
                this.postMessage({
                  id: data.id,
                  type: constants.messages.REQUEST_APP_CONTROL_RESPONSE,
                  status: constants.statuses.REQUEST_CONTROL_READY,
                });
              })
              .catch((error) => {
                this.postMessage({
                  id: data.id,
                  type: constants.messages.REQUEST_APP_CONTROL_RESPONSE,
                  status: constants.statuses.REQUEST_CONTROL_FAILED,
                  error,
                });
              });
          }

          break;
        }

        case constants.messages.REQUEST_APP_CONTROL_RESPONSE: {
          if (this.id !== data.id) return;

          this.trigger(constants.events.REQUEST_APP_CONTROL_RESPONSE, {
            id: this.id,
            status: data.status,
            error: data.error,
          });
          break;
        }

        default:
      }
    });
  }

  checkAppInstances() {
    if (!this.broadcastChannel) return null;

    return new Promise((resolve) => {
      let timer = null;
      const onEvent = (...args) => {
        clearTimeout(timer);
        resolve(...args);
      };

      this.on(constants.events.APP_INSTANCE_LOADED_RESPONSE, onEvent, { once: true });

      timer = setTimeout(() => {
        resolve({});
        this.off(constants.events.APP_INSTANCE_LOADED_RESPONSE, onEvent);
      }, constants.RESPONSE_TIMEOUT);

      this.postMessage({ id: this.id, type: constants.messages.APP_INSTANCE_LOADED });
    });
  }

  requestAppControl() {
    if (!this.broadcastChannel) return null;

    return new Promise((resolve, reject) => {
      let timer = null;

      const requestAppControlResponse = (data) => {
        const resolvePromise = () => {
          resolve(data);
          this.off(constants.events.REQUEST_APP_CONTROL_RESPONSE, requestAppControlResponse);
        };

        switch (data.status) {
          case constants.statuses.REQUEST_CONTROL_FAILED: {
            clearTimeout(timer);
            this.off(constants.events.REQUEST_APP_CONTROL_RESPONSE, requestAppControlResponse);
            reject(data.error);
            break;
          }

          case constants.statuses.REQUEST_CONTROL_PENDING: {
            clearTimeout(timer);

            // Cover the case when after getting `REQUEST_CONTROL_PENDING` status, the other tab doesn't respond with `REQUEST_CONTROL_READY`
            timer = setTimeout(resolvePromise, constants.REQUEST_CONTROL_RESPONSE_TIMEOUT);
            break;
          }

          case constants.statuses.REQUEST_CONTROL_READY: {
            resolvePromise();
            break;
          }

          default:
        }
      };

      this.on(constants.events.REQUEST_APP_CONTROL_RESPONSE, requestAppControlResponse);

      timer = setTimeout(() => {
        resolve();
        this.off(constants.events.REQUEST_APP_CONTROL_RESPONSE, requestAppControlResponse);
      }, constants.RESPONSE_TIMEOUT);

      this.postMessage({
        id: this.id,
        type: constants.messages.REQUEST_APP_CONTROL,
      });
    });
  }

  postMessage(data) {
    if (!this.broadcastChannel) return;

    this.broadcastChannel.postMessage(data);
  }

  trigger(event, ...args) {
    if (this.observers.has(event)) {
      this.observers.get(event).forEach((callback) => {
        if (callback[OPTIONS_SYMBOL] && callback[OPTIONS_SYMBOL].once) {
          this.off(event, callback);
        }

        callback(...args);
      });
    }
  }

  on(event, callback, options = {}) {
    if (typeof callback !== 'function') throw new Error('callback is not a function');
    const handler = callback;

    handler[OPTIONS_SYMBOL] = options;

    if (this.observers.has(event)) {
      const handlers = this.observers.get(event);
      this.observers.set(event, [...handlers, handler]);
      return;
    }

    this.observers.set(event, [handler]);
  }

  off(event, callback) {
    if (!this.observers.has(event)) return;

    const handlers = this.observers.get(event);
    this.observers.set(
      event,
      handlers.filter((handler) => handler !== callback)
    );
  }
}

export default AppControlDriver;
